import { startHtml5MediaTrackingTypeSafe } from '@montugroup/data-collection';
import PlayCircle from '@mui/icons-material/PlayCircle';
import { Box } from '@mui/material';
import type { FC, KeyboardEvent, MouseEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Video } from '~/components/molecules/Video';
import { getLogger } from '~/lib/logger/logger';
import { getCompoundTestIdGenerator } from '~/utils/getCompoundTestIdGenerator';
import type { VideoPlayerProps } from './VideoPlayer.types';

const logger = getLogger('VideoText');

/**
 * Displays the video player with play/pause functionality, and snowplow
 * tracking.
 */
export const VideoPlayer: FC<VideoPlayerProps> = ({ id, poster, sources }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const getCompoundTestId = getCompoundTestIdGenerator('videoPlayer');

  const handleVideoClick = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const videoElement = videoRef.current;
    if (!videoElement) {
      return;
    }

    if (videoElement.paused) {
      videoElement.play();
    } else {
      videoElement.pause();
    }
  };

  const handleVideoKeyDown = (e: KeyboardEvent) => {
    e.preventDefault();
    const videoElement = videoRef.current;
    if (!videoElement) {
      return;
    }

    if (e.key === 'Enter') {
      if (videoElement.paused) {
        videoElement.play();
      } else {
        videoElement.pause();
      }
    }
  };

  const handlePlay = () => {
    const videoElement = videoRef.current;
    if (!videoElement) {
      return;
    }

    setIsPlaying(true);
    if (!videoElement.controls) {
      videoElement.controls = true;
    }
  };

  const handlePause = () => {
    const videoElement = videoRef.current;
    if (!videoElement) {
      return;
    }

    // Check if video is paused after 100ms to prevent flickering while changing the video tracking
    setTimeout(() => {
      if (videoElement.paused) {
        setIsPlaying(false);
      }
    }, 100);
  };

  // Snowplow tracking
  useEffect(() => {
    const track = async () => {
      if (id && videoRef.current) {
        try {
          await startHtml5MediaTrackingTypeSafe({
            id,
            video: videoRef.current,
            label: 'Video Player',
          });
        } catch (error) {
          logger.error('Video Text: Error starting HTML5 media tracking', { error });
        }
      }
    };

    track();
  }, [id]);

  return (
    <Box
      onClick={handleVideoClick}
      sx={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
      }}
    >
      <Video
        controlsList="nodownload"
        data-testid={getCompoundTestId('video')}
        {...(poster && { poster })}
        onKeyDown={handleVideoKeyDown}
        onPause={handlePause}
        onPlay={handlePlay}
        playsInline
        preload="metadata"
        ref={videoRef}
        sources={sources}
        sx={{
          borderRadius: 6,
          overflow: 'hidden',
        }}
        tabIndex={0}
      />

      {!isPlaying && (
        <PlayCircle
          sx={{
            fontSize: 60,
            left: '50%',
            position: 'absolute',
            top: '50%',
            transform: 'translate(-50%, -50%)',
          }}
          color="primary"
        />
      )}
    </Box>
  );
};
